import {FC, useState} from "react";
import useAuthContext from "contexts/Auth/useAuthContext";
import {handleError} from "helpers/errors";
import {Copy} from "lucide-react";
import {activityStatusByDate} from "utils/activityStatusByDate";
import SkillGroupsBreakdown from "pages/accountant/Profile/components/SkillGroupsBreakdown";
import GroupComparison from "pages/accountant/Profile/components/GroupComparison";
import LifetimeSkillChart from "pages/accountant/Profile/components/LifetimeSkillChart/LifetimeSkillChart";
import RecentTrainingActivity from "pages/accountant/Profile/components/RecentTrainingActivity";
import ProgressTags from "pages/accountant/Profile/components/ProgressTags";
import FoundationalKnowledge from "pages/accountant/Profile/components/FoundationalKnowledge";
import Certification from "pages/accountant/Profile/components/ProfileCard/Certification";
import Review from "pages/accountant/Profile/components/ProfileCard/Review";
import {IAccountant} from "api/types";
import {useNavigate} from "react-router";
import ProfileImage from "components/ProfileImage";
import {DefaultProfileSrc} from "assets/images";
import Tooltip from "components/Tooltip";
import recruiterApi from "api/recruiter";
import api from "api";
import {MapPin, Clock, Monitor, Star, Activity, Zap, Users, Info, MessageCircle, BookmarkPlus} from "lucide-react";
import {extractUrl} from "pages/accountant/Profile/utils";
import {ACCOUNTANT_BIO_TRUNCATE_LENGTH} from "pages/accountant/Profile/constants";

type Level = "low" | "average" | "high";

function getOverviewConfig(knowledge: Level, activity: Level, speed: Level) {
  if (activity === "low") return {text: "Inactive", color: "#F57A47"};
  if (activity === "average") return {text: "Inconsistent", color: "#F5A623"};
  if (knowledge === "high" && speed === "high") return {text: "Exceptional Talent!", color: "#57B98F"};
  if (knowledge === "high") return {text: "Top Talent", color: "#57B98F"};
  if (speed === "high") return {text: "Rising Talent", color: "#57B98F"};
  if (speed === "average" && knowledge === "average") return {text: "Above Average", color: "#57B98F"};
  if (speed === "low" && knowledge === "low") return {text: "Below Average", color: "#F57A47"};
  return {text: "Average Learner", color: "#F5A623"};
}

function levelColor(level: Level) {
  return level === "high" ? "#57B98F" : level === "average" ? "#F5A623" : "#F57A47";
}
function levelLabel(level: Level) {
  return level === "high" ? "High" : level === "average" ? "Average" : "Low";
}
const knowledgeLabelMap: Record<Level, string> = {high: "Excellent", average: "Average", low: "Low"};
const capitalize = (s?: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : undefined);

const LoadedAccountantProfilePage: FC<{user: IAccountant}> = ({user}) => {
  const navigate = useNavigate();
  const auth = useAuthContext();
  const [moreInfoOpen, setMoreInfoOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"applied" | "foundational">("applied");
  const [contacting, setContacting] = useState(false);
  const [addingToWatchlist, setAddingToWatchlist] = useState(false);
  const isEnterpriseOwner = !!user.enterprise && auth?.uid === user.enterprise;

  const onCopyMagic = () => {
    if (!user.magic) {
      handleError("No magic link available");
      return;
    }
    navigator.clipboard
      .writeText(`https://fastandeasyqbo.booksmatch.us/auth/code/${user.magic}`)
      .then(() => handleError("Magic link copied to clipboard"))
      .catch(() => handleError("Failed to copy magic link"));
  };

  const onContact = () => {
    if (contacting || addingToWatchlist) return;
    setContacting(true);
    api
      .createContact(user.id)
      .then((chatId: string) => navigate("/chat/" + chatId))
      .finally(() => setContacting(false));
  };

  const onAddToWatchlist = () => {
    if (contacting || addingToWatchlist) return;
    setAddingToWatchlist(true);
    recruiterApi
      .addCandidate(user.id)
      .then(() => navigate("/candidates"))
      .finally(() => setAddingToWatchlist(false));
  };

  const knowledge: Level = user.skillScore > 80 ? "high" : user.skillScore > 20 ? "average" : "low";
  const activity: Level = activityStatusByDate(user.xpData.lastActivity || user.xpData.dateJoined);
  const speed: Level = user.speed;

  const overview = getOverviewConfig(knowledge, activity, speed);
  const [city, state] = user.location?.split(", ") || [];

  const statusChips = [
    {
      label: overview.text,
      sublabel:
        activity === "low" ? "Low activity" : activity === "average" ? "Activity inconsistent" : "Knowledge + Activity",
      color: overview.color,
      icon: <Users className="w-5 h-5" />,
    },
    {
      label: knowledgeLabelMap[knowledge],
      sublabel: "Knowledge",
      color: levelColor(knowledge),
      icon: <Star className="w-5 h-5" />,
    },
    {
      label: levelLabel(activity),
      sublabel: "Activity",
      color: levelColor(activity),
      icon: <Activity className="w-5 h-5" />,
    },
    {label: levelLabel(speed), sublabel: "Speed", color: levelColor(speed), icon: <Zap className="w-5 h-5" />},
  ];

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden bg-brand-cream">
      <div className="px-8 py-7">
        <h1 className="text-2xl font-bold text-brand-navy mb-5">{user.name || "Accountant Profile"}</h1>

        {/* Status bar */}
        {!user.enterprise && (
          <div className="rounded-2xl overflow-hidden mb-5 border border-brand-border bg-white">
            <div className="flex h-1.5">
              {statusChips.map((chip, i) => (
                <div key={i} className="flex-1" style={{backgroundColor: chip.color}} />
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-brand-border">
              {statusChips.map((chip, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-4">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-white"
                    style={{backgroundColor: chip.color}}
                  >
                    {chip.icon}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1">
                      <span className="font-bold text-sm text-brand-navy">{chip.label}</span>
                      <Tooltip position="right" text={chip.sublabel} className="bg-slate-800">
                        <Info className="w-3.5 h-3.5 text-brand-muted flex-shrink-0 cursor-default" />
                      </Tooltip>
                    </div>
                    <p className="text-xs text-brand-muted">{chip.sublabel}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Profile card */}
        <div className="rounded-2xl p-6 mb-5 border border-brand-border bg-white">
          <div className="flex items-start gap-5">
            <div className="flex-shrink-0">
              <ProfileImage
                src={user.image || DefaultProfileSrc}
                size={72}
                className="w-18 h-18 rounded-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h2 className="text-xl font-bold text-brand-navy">{user.name || "—"}</h2>
                {!user.enterprise && user.linkedIn && (
                  <a
                    href={user.linkedIn}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium hover:opacity-80 transition-opacity"
                    style={{backgroundColor: "#0A66C2", color: "white"}}
                  >
                    LinkedIn
                  </a>
                )}
                {!user.enterprise && user.alignable && (
                  <a
                    href={user.alignable}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium hover:opacity-80 transition-opacity"
                    style={{backgroundColor: "#E8401C", color: "white"}}
                  >
                    Alignable
                  </a>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-4 mb-3">
                {user.location && (
                  <span className="flex items-center gap-1.5 text-sm text-brand-muted">
                    <MapPin className="w-3.5 h-3.5" />
                    {[capitalize(city), state?.toUpperCase()].filter(Boolean).join(", ")}
                  </span>
                )}
                {user.employmentType && !user.enterprise && (
                  <span className="flex items-center gap-1.5 text-sm text-brand-muted">
                    <Clock className="w-3.5 h-3.5" />
                    {user.employmentType}
                  </span>
                )}
                {Object.keys(user.softwareExperience).length > 0 && (
                  <span className="flex items-center gap-1.5 text-sm text-brand-muted">
                    <Monitor className="w-3.5 h-3.5" />
                    {Object.keys(user.softwareExperience).join(", ")}
                  </span>
                )}
              </div>
              {user.bio && (
                <p className="text-sm text-brand-navy leading-relaxed">
                  {moreInfoOpen
                    ? user.bio
                    : user.bio.slice(0, ACCOUNTANT_BIO_TRUNCATE_LENGTH) +
                      (user.bio.length > ACCOUNTANT_BIO_TRUNCATE_LENGTH ? "…" : "")}
                  {user.bio.length > ACCOUNTANT_BIO_TRUNCATE_LENGTH && (
                    <button
                      onClick={() => setMoreInfoOpen(!moreInfoOpen)}
                      className="ml-1 text-sm font-medium text-brand-orange underline underline-offset-2 hover:opacity-70 transition-opacity"
                    >
                      {moreInfoOpen ? "Show less" : "Read more"}
                    </button>
                  )}
                </p>
              )}
            </div>
          </div>
          <div className="mt-5 pt-5 border-t border-brand-border">
            <p className="text-xs font-semibold uppercase tracking-widest text-brand-muted mb-3">
              Credentials &amp; Badges
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.keys(user.certifications || {}).length > 0 ? (
                Object.entries(user.certifications || {}).map(([name, val]) => (
                  <Certification
                    key={name}
                    name={name}
                    url={extractUrl(val)}
                    imageUrl={typeof val === "object" ? val.imageUrl : undefined}
                    tooltipPosition="bottom"
                  />
                ))
              ) : (
                <p className="text-xs text-brand-muted italic">No certifications added yet.</p>
              )}
            </div>
          </div>

          {/* Recruiter CTAs */}
          <div className="mt-5 pt-5 border-t border-brand-border flex items-center gap-3">
            <button
              onClick={onContact}
              disabled={contacting || addingToWatchlist}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{backgroundColor: "#3D52AD"}}
            >
              <MessageCircle className="w-4 h-4" />
              Message {user.enterprise ? "Employee" : "Candidate"}
            </button>
            <button
              onClick={onAddToWatchlist}
              disabled={contacting || addingToWatchlist}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl border border-brand-border text-brand-navy hover:border-brand-navy transition-colors disabled:opacity-50"
            >
              <BookmarkPlus className="w-4 h-4" />
              Save to Shortlist
            </button>
            {isEnterpriseOwner && (
              <button
                onClick={onCopyMagic}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl border border-brand-border text-brand-navy hover:border-brand-navy transition-colors"
              >
                <Copy className="w-4 h-4" />
                Copy Magic Link
              </button>
            )}
          </div>
        </div>

        {/* Reviews row */}
        {!user.enterprise && (
          <div className="rounded-2xl px-6 py-4 mb-5 border border-brand-border bg-white flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-muted mr-1">Reviews</span>
            {Object.keys(user.reviews || {}).length > 0 ? (
              Object.entries(user.reviews || {}).map(([name, val]) => (
                <Review key={name} name={name} url={extractUrl(val)} />
              ))
            ) : (
              <p className="text-xs text-brand-muted italic">No reviews added yet.</p>
            )}
          </div>
        )}

        {!!user.enterprise && <ProgressTags tags={user.activityOverview.enterpriseProgressTags || []} />}

        {/* Tabs */}
        <div>
          <div className="flex items-end" style={{position: "relative", zIndex: 1}}>
            {(["applied", "foundational"] as const).map((tab) => {
              const isActive = activeTab === tab;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className="flex-1 px-6 py-3 text-sm transition-all relative"
                  style={{
                    backgroundColor: isActive ? "white" : "#EDE9E3",
                    color: isActive ? "#3D52AD" : "#8490AA",
                    fontWeight: isActive ? 700 : 500,
                    border: "1px solid #E8E4DF",
                    borderBottom: isActive ? "1px solid white" : "1px solid #E8E4DF",
                    borderRadius: "12px 12px 0 0",
                    marginBottom: isActive ? "-1px" : "0",
                    marginRight: "2px",
                  }}
                >
                  {tab === "applied" ? "Applied Skills" : "Foundational Knowledge"}
                </button>
              );
            })}
          </div>
          <div className="rounded-b-2xl rounded-tr-2xl border border-brand-border bg-white p-6">
            <div style={{display: activeTab === "applied" ? "block" : "none"}}>
              <div className="grid grid-cols-2 2xl:grid-cols-4 gap-4">
                <SkillGroupsBreakdown user={user} />
                <GroupComparison user={user} />
                <LifetimeSkillChart user={user} />
                <RecentTrainingActivity user={user} />
              </div>
            </div>
            <div style={{display: activeTab === "foundational" ? "block" : "none"}}>
              <FoundationalKnowledge talentId={user.id} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadedAccountantProfilePage;
